import * as React from 'react';
import {useRef, useEffect, useMemo, useState} from 'react';
import {provider, AnimatePresence, view, createView, assignProps} from '@sail/engine';
import {BaseDrawer, DrawerContext} from './BaseDrawer';
import {DrawerConfirmationDialog} from './DrawerConfirmationDialog';
import {
  bottomSheetAnimation,
  bottomSheetStyles,
  fullscreenSheetStyles,
  fullscreenAnimation,
} from '../../utilStyles/sheet';
import {useNestedSheets} from '../../hooks/useNestedSheets';
import type {DrawerProps} from './DrawerProps';

export const DrawerMobile = createView<DrawerProps>(
  ({
    children,
    confirmCloseMessages,
    preferredMobileDisplay,
    header,
    footer,
    onClose,
    ...props
  }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const drawerRef = useRef<HTMLDivElement>(null);
    const finalMobileDisplay = preferredMobileDisplay || 'fullscreen-sheet';
    const {sheetStackProvider} = useNestedSheets(drawerRef, finalMobileDisplay);

    useEffect(() => {
      drawerRef.current?.focus();
    }, [drawerRef]);

    const onConfirmClose = () => {
      if (confirmCloseMessages) {
        setShowConfirm(true);
      } else {
        onClose && onClose();
      }
    };

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onConfirmClose();
      }
    }

    const mobileStyles = useMemo(() => {
      return finalMobileDisplay === 'bottom-sheet'
        ? bottomSheetStyles
        : fullscreenSheetStyles;
    }, [finalMobileDisplay]);

    return (
      <BaseDrawer
        // eslint-disable-next-line react/jsx-no-bind
        onKeyDown={onKeyDown}
        {...props}
        ref={drawerRef}
        onClose={onConfirmClose}
        uses={[
          provider(DrawerContext, {onClose: onConfirmClose}),

          mobileStyles,

          bottomSheetAnimation(finalMobileDisplay !== 'bottom-sheet'),
          fullscreenAnimation(finalMobileDisplay !== 'fullscreen-sheet'),

          finalMobileDisplay === 'bottom-sheet' ? sheetStackProvider : null,
        ]}
        header={header}
        subviews={{
          layer: assignProps({iosScrollLock: true})
        }}
      >
        <view.div
          css={{
            position: 'relative',
            overflowY: 'auto',
            overscrollBehaviorY: 'contain',
            flexShrink: 1,
          }}
        >
          {children}
        </view.div>
        <view.div css={{height: 'fit'}}>{footer}</view.div>
        <AnimatePresence>
          {confirmCloseMessages && showConfirm ? (
            <DrawerConfirmationDialog
              messages={confirmCloseMessages}
              onClose={onClose}
              onCancel={() => setShowConfirm(false)}
            />
          ) : null}
        </AnimatePresence>
      </BaseDrawer>
    );
  },
);
